import React, { useState } from 'react';
import { useAuthStore, useThemeStore } from '@/stores';
import { apiClient } from '@/services/api-client';
import { fetchFTTHToken } from '@/services/api-client';
import { setTokens } from '@/utils/auth';
import { X, Lock, KeyRound, ShieldAlert, CheckCircle2, Eye, EyeOff, RefreshCw, AlertTriangle } from 'lucide-react';

interface UpdatePortalCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const UpdatePortalCredentialsModal: React.FC<UpdatePortalCredentialsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user, savedUsername, setSavedCredentials, setPortalAuthWarning } = useAuthStore() as any;
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const defaultUser = (user?.username || savedUsername || 'city').toLowerCase().trim();
  const [username, setUsername] = useState(defaultUser);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setErrorMessage('يرجى إدخال كلمة المرور الجديدة');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // 1. Verify with FTTH portal auth directly or via backend
      let tokenData: any = null;
      try {
        tokenData = await fetchFTTHToken(username.trim(), password.trim());
      } catch (directErr: any) {
        console.warn('[Credentials] Direct token fetch returned error, verifying via backend API...', directErr);
      }

      // 2. Call backend update-credentials endpoint to persist new password & token
      const res = await apiClient.post('/contractor/update-credentials', {
        username: username.trim(),
        newPassword: password.trim(),
      });

      const data = res?.data || {};

      if (data.success || tokenData?.access_token) {
        const freshAccessToken = data.access_token || tokenData?.access_token;
        const freshRefreshToken = data.refresh_token || tokenData?.refresh_token || '';

        if (freshAccessToken) {
          setTokens(freshAccessToken, freshRefreshToken);
        }

        setSavedCredentials(username.trim(), password.trim());
        setPortalAuthWarning(null);
        setSuccessMessage('تم التحقق وتحديث كلمة المرور بنجاح! تم استئناف الاتصال ببوابة FTTH.');

        if (onSuccess) {
          onSuccess();
        }

        setTimeout(() => {
          onClose();
        }, 1800);
      } else {
        setErrorMessage(data.error || 'تعذر التحقق من كلمة المرور مع خادم FTTH');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'اسم المستخدم أو كلمة المرور غير صحيحة على بوابة FTTH';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '16px',
        direction: 'rtl',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '460px',
          background: isLight ? '#ffffff' : 'linear-gradient(135deg, #0d1f3c 0%, #081326 100%)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '20px',
          boxShadow: isLight
            ? '0 20px 40px -10px rgba(0,0,0,0.15)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 30px rgba(59, 130, 246, 0.15)',
          padding: '24px',
          position: 'relative',
          color: isLight ? '#0f172a' : '#f8fafc',
          animation: 'modalFadeIn 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes modalFadeIn {
            from { transform: scale(0.95) translateY(10px); opacity: 0; }
            to { transform: scale(1) translateY(0); opacity: 1; }
          }
        `}</style>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
              }}
            >
              <KeyRound size={22} />
            </div>
            <div>
              <h2 style={{ fontSize: '17px', fontWeight: 800, margin: '0 0 2px 0', color: isLight ? '#0f172a' : '#ffffff' }}>
                تحديث كلمة مرور بوابة FTTH
              </h2>
              <p style={{ fontSize: '12px', color: isLight ? '#64748b' : '#94a3b8', margin: 0 }}>
                تجديد بيانات الدخول لاستئناف جلب التذاكر والمشتركين والتنبيهات
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px',
              color: isLight ? '#64748b' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Info Box */}
        <div
          style={{
            background: isLight ? '#fef3c7' : 'rgba(245, 158, 11, 0.1)',
            border: isLight ? '1px solid #fde68a' : '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            padding: '12px 14px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '12px', color: isLight ? '#92400e' : '#fcd34d', lineHeight: 1.5 }}>
            عند تغيير كلمة المرور على بوابة FTTH المركزية، يتوقف النظام عن جلب التذاكر وإرسال التنبيهات في الخلفية حتى يتم إدخال كلمة المرور الجديدة والتحقق منها.
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Username */}
          <div>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, marginBottom: '6px', color: isLight ? '#334155' : '#cbd5e1' }}>
              اسم المستخدم (Username)
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.05)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                padding: '10px 14px',
                color: isLight ? '#0f172a' : '#f8fafc',
                fontSize: '13.5px',
                outline: 'none',
                direction: 'ltr',
                textAlign: 'right',
              }}
              placeholder="أدخل اسم المستخدم..."
            />
          </div>

          {/* New Password */}
          <div>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, marginBottom: '6px', color: isLight ? '#334155' : '#cbd5e1' }}>
              كلمة المرور الجديدة في بوابة FTTH
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.05)',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  padding: '10px 42px 10px 14px',
                  color: isLight ? '#0f172a' : '#f8fafc',
                  fontSize: '13.5px',
                  outline: 'none',
                  direction: 'ltr',
                  textAlign: 'right',
                }}
                placeholder="أدخل كلمة المرور الجديدة..."
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: isLight ? '#94a3b8' : '#64748b',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div
              style={{
                background: isLight ? '#fee2e2' : 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '12px',
                color: isLight ? '#b91c1c' : '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                lineHeight: 1.4,
              }}
            >
              <ShieldAlert size={16} style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div
              style={{
                background: isLight ? '#dcfce7' : 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '10px',
                padding: '10px 12px',
                fontSize: '12.5px',
                color: isLight ? '#166534' : '#4ade80',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 600,
              }}
            >
              <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 18px',
                color: '#ffffff',
                fontSize: '13.5px',
                fontWeight: 700,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.2s',
              }}
            >
              {isLoading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>جاري التحقق من خادم FTTH...</span>
                </>
              ) : (
                <>
                  <Lock size={16} />
                  <span>التحقق وحفظ كلمة المرور</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                padding: '12px 18px',
                color: isLight ? '#475569' : '#94a3b8',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
